import { useQuery } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import { PageNotFound } from "components/simple";
import { GetRole } from "graphql/user/user.gql";
import { useDispatch, useTokenExpiration } from "hooks";
import { isLogin } from "utils/auth";
import TokenExpirationModal from "./TokenExpirationModal";

const Home: React.FC = () => <Redirect to="/assessments" />;

const AdminManagement = React.lazy(() => import("components/pages/AdminManagement"));
const Assessments = React.lazy(() => import("components/pages/Assessments"));
const Coaching = React.lazy(() => import("components/pages/Coaching"));
const CoachingCall = React.lazy(() => import("components/pages/Coaching/CoachingCall"));
const CoachingSettings = React.lazy(() => import("components/pages/Coaching/CoachingSettings"));
const Help = React.lazy(() => import("components/pages/Help"));
const Organizations = React.lazy(() => import("components/pages/Organizations"));
const Organization = React.lazy(() => import("components/pages/Organization"));
const Pdf = React.lazy(() => import("components/pages/Pdf"));
const Settings = React.lazy(() => import("components/pages/Settings"));
const User = React.lazy(() => import("components/pages/User"));
const UserAssessments = React.lazy(() => import("components/pages/UserAssessments"));
const UserManagement = React.lazy(() => import("components/pages/UserManagement"));
const FinalizeScores = React.lazy(() => import("components/pages/UserAssessments/FinalizeScores"));
const NewIndex = React.lazy(() => import("components/pages/NewIndex"));
const InternalCycleNotes = React.lazy(() =>
  import("components/pages/UserAssessments/InternalCycleNotes")
);
const SingleUserAssessment = React.lazy(() =>
  import("components/pages/UserAssessments/SingleUserAssessment")
);

const ProtectedRoutes: React.FC = () => {
  const dispatch = useDispatch();

  const {
    expirationTime,
    isTokenExpiring,
    hasTokenExpired,
    handleTokenRefresh
  } = useTokenExpiration();

  const [showTokenExpirationModal, setShowTokenExpirationModal] = useState(false);

  useEffect(() => {
    if (isTokenExpiring) {
      setShowTokenExpirationModal(true);
    } else {
      setShowTokenExpirationModal(false);
    }
  }, [isTokenExpiring]);

  const { data: roleData } = useQuery(GetRole, { fetchPolicy: "no-cache" });

  useEffect(() => {
    const role = roleData?.me?.role;
    dispatch({ type: "set_role", value: role });
  }, [roleData, dispatch]);

  if (!isLogin() || hasTokenExpired) {
    return <Redirect push to="/login" />;
  }

  return (
    <>
      <Switch>
        <Route exact path="/" component={Home} />
        <Route path="/assessments" component={Assessments} />
        <Route path="/admin-management" component={AdminManagement} />
        <Route path="/user-management" component={UserManagement} />
        <Route path="/user-assessments/:id/new-index" component={NewIndex} />
        <Route path="/user-assessments/:id/finalize" component={FinalizeScores} />
        <Route path="/user-assessments/:id" component={UserAssessments} />
        <Route path="/organizations/:id" component={Organization} />
        <Route path="/organizations" component={Organizations} />
        <Route path="/settings" component={Settings} />
        <Route path="/users/:userId/coaching-sessions" exact component={Coaching} />
        <Route
          path="/users/:userId/coaching-sessions/:coachingSessionId"
          component={CoachingCall}
        />
        <Route path="/coaching-settings" component={CoachingSettings} />
        <Route path="/assessment/:id" component={SingleUserAssessment} />
        <Route path="/users/:id" component={User} />
        <Route path="/internal-notes/:id" component={InternalCycleNotes} />
        <Route path="/help" component={Help} />
        <Route path="/pdf/:userId" component={Pdf} />
        <Route component={PageNotFound} />
      </Switch>

      <TokenExpirationModal
        show={showTokenExpirationModal}
        expirationTime={expirationTime}
        onAccept={async () => {
          await handleTokenRefresh();
          setShowTokenExpirationModal(false);
        }}
      />
    </>
  );
};

export default ProtectedRoutes;
